import { TrainingTrack, TrainingTrackProgress, TrainingTrackScore } from '../../../../shared/src/trainingTrack.types';
import { Socket } from 'socket.io-client';

/**
 * Service for managing training tracks
 */
export class TrainingTrackService {
    private socket: Socket;
    private tracks: Map<string, TrainingTrack> = new Map();
    private scores: Map<string, TrainingTrackScore[]> = new Map();
    
    /**
     * Constructor
     * @param socket The socket.io client instance
     */
    constructor(socket: Socket) {
        this.socket = socket;
        this.setupSocketListeners();
    }
    
    /**
     * Sets up socket event listeners
     */
    private setupSocketListeners(): void {
        // Listen for track data from server
        this.socket.on('trainingTrack:tracks', (tracks: TrainingTrack[]) => {
            console.log(`[TrainingTrackService] Received ${tracks.length} tracks from server`);
            
            // Store tracks in local cache
            tracks.forEach(track => {
                this.tracks.set(track.id, track);
            });
            
            // Dispatch event that tracks have been loaded
            document.dispatchEvent(new CustomEvent('trainingTrack:tracksLoaded', { 
                detail: { tracks } 
            }));
        });
        
        // Listen for score updates from server
        this.socket.on('trainingTrack:scores', (data: { trackId: string, scores: TrainingTrackScore[] }) => {
            console.log(`[TrainingTrackService] Received scores for track ${data.trackId}`);
            
            // Store scores in local cache
            this.scores.set(data.trackId, data.scores);
            
            // Dispatch event that scores have been loaded
            document.dispatchEvent(new CustomEvent('trainingTrack:scoresLoaded', { 
                detail: { 
                    trackId: data.trackId,
                    scores: data.scores 
                } 
            }));
        });
    }
    
    /**
     * Requests all available training tracks from the server
     */
    public requestTracks(): void {
        console.log('[TrainingTrackService] Requesting tracks from server');
        this.socket.emit('trainingTrack:requestTracks');
    }
    
    /**
     * Requests scores for a specific track from the server
     * @param trackId The ID of the track to get scores for
     */
    public requestScores(trackId: string): void {
        console.log(`[TrainingTrackService] Requesting scores for track ${trackId}`);
        this.socket.emit('trainingTrack:requestScores', { trackId });
    }
    
    /**
     * Submits a track score to the server
     * @param score The score to submit
     */
    public submitScore(score: TrainingTrackScore): void {
        console.log(`[TrainingTrackService] Submitting score for track ${score.trackId}`);
        this.socket.emit('trainingTrack:submitScore', score);
    }
    
    /**
     * Submits track progress to the server
     * @param progress The progress to submit
     */
    public submitProgress(progress: TrainingTrackProgress): void {
        console.log(`[TrainingTrackService] Submitting progress for track ${progress.trackId}`);
        this.socket.emit('trainingTrack:submitProgress', progress);
    }
    
    /**
     * Gets a track by ID
     * @param trackId The ID of the track to get
     * @returns The track or null if not found
     */
    public getTrack(trackId: string): TrainingTrack | null {
        return this.tracks.get(trackId) || null;
    }
    
    /**
     * Gets all available tracks
     * @returns Array of all tracks
     */
    public getAllTracks(): TrainingTrack[] {
        return Array.from(this.tracks.values());
    }
    
    /**
     * Gets scores for a specific track
     * @param trackId The ID of the track to get scores for
     * @returns Array of scores or empty array if none found
     */
    public getScores(trackId: string): TrainingTrackScore[] {
        return this.scores.get(trackId) || [];
    }
}